import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import Sifter from 'scoreboard/components/scoreboard-sifter'
import selects from 'scoreboard/components/selects'
import principalUtil from 'scoreboard/components/principals'
import groupType from 'scoreboard/admin/principals/groups/group-type'
import Immutable from 'immutable'
const Set = Immutable.Set
import SearchView from 'scoreboard/components/search-view'
import * as d3 from 'd3-selection'

/**
 * Returns a version of the given object but with all properties removed that
 * have values of null, undefined, or an empty string
 */
function cleanEmptyStrings(obj) {
	return _.transform(obj, function(result, value, key) {
		if (!_.isNil(value) && value != '') {
			result[key] = value
		}
	})
}

/**
 * A lazy select showing a list of principals
 * @extends SearchView
 */
var MultiPrincipalChooser = SearchView.extend(/** @lends MultiPrincipalChooser.prototype */{

	/**
	 * @constructs
	 *
	 * @param {String|Function} [options.url=`api/principals`] points to the data that should be shown in the select
	 *
	 * @param options.currentPrincipals the principals already selected (and thus not eligible for selection)
	 *
	 * @param {Principal|Principal[]} [options.exclude] principal(s) to exclude (e.g. don't let
	 *  a group become a member of itself #1159).  Must be a User or Group (id/type required)
	 *
	 * @param [options.cache] cached version of the data from the above url
	 *
	 * @param {String} [options.groupBy] see selects.sortOptions
	 *
	 * @param {String[]} [options.groupSortOrder] see selects.sortOptions
	 *
	 * @param {String} [options.groupSortAttribute=type] see selects.sortOptions
	 *
	 * @param {boolean} [options.hideSelectedOptions=false] if true, the selected options won't appear in the select control
	 *
	 * @param {String} [options.placeholder=Start Typing]
	 *
	 * @param {Function} [optionDecoratorFn] will be invoked as each option is added
	 * (before they are sorted), allowing additional attributes (e.g. 'group') to
	 * be applied.
	 */
	initialize: function(options = {}) {
		SearchView.prototype.initialize.apply(this, arguments)
		this.url = this.options.url || `api/principals`
		this.selectedSet = this._createImmutableSet(options.currentPrincipals)
		this.multiple = _.get(this.options, 'multiple', true)
		this.hideSelectedOptions = _.get(this.options, 'hideSelectedOptions', false)
		this.excluded = this.options.exclude ? _.flatten([this.options.exclude]) : []
	},

	events: {
		'click': 'show',
		'change select': 'selectPrincipal'
	},

	/**
	 * Shows the select. The passed in options are provided to scoreboard-select
	 *
	 * @param [options.open=true]
	 *
	 * @param [options.options] options to display in the select
	 */
	show: function(options = {}) {
		if (this.shown && this.multiple) {
			// Re-render the select in case the user previously filtered the options
			// and then closed the menu and re-opened it. This allows all of the available
			// options to show up again instead of the only showing the  previously
			// filtered options. #31802
			this._renderSelect({ options: this.allPrincipals })
			return
		}

		// hide the placeholder and show the select
		this.$('.placeholder, .add-controls').toggleClass('is-not-displayed')

		// render the select without loading dots
		this._renderSelect(options)
		this.search(null, _.extend({_initial:true}, options)) // loads the options from the server
		this.createLoadingDots()

		if (!this.shown) {
			this.trigger('show')
		}
		this.shown = true
	},

	populateDomDataset: function(principal) {
		let groupTypeKey = principal.domainType ? principal.type : principal.groupType
		principal.dataset = {
			type: principal.type,
			'group': principal.group,
			'domain-type': principal.domainType,
			'group-type': groupTypeKey,
			'post-label': _.isNil(groupTypeKey) ? '': '(' + groupType.label(groupTypeKey) + ')',
			icon: principalUtil.getIconForType(principal.type)
		}
	},

	_createImmutableSet: function(principals) {
		return Set(_.map(principals, p => this._createImmutablePrincipal(p)))
	},

	/**
	 * Returns a normalized object that can be safely added and removed from an
	 * ImmutableSet. Converts the ID to an int, and wraps it up in an Immutable object
	 */
	_createImmutablePrincipal(p) {
		const {optionDecoratorFn} = this.options
		if (optionDecoratorFn) {
			optionDecoratorFn(p)
		}

		//include the group so that the option returned includes the group in the
		//select it belongs too
		const object = _.pick(p, ['id', 'type', 'group', 'label', 'groupType', 'email'])
		object.id = Number(object.id)
		object.label = _.trim(object.label)
		return Immutable.fromJS(cleanEmptyStrings(object))
	},

	/**
	 * Returns and array of principals that are currently selected in the dropdown.
	 * The principal objects in the array are assembled by the principalFromOption method,
	 * see that method for the details of the principal object
	 */
	_selectedPrincipals: function() {
		let selectedOptions = this.$('select option:selected')
		return selectedOptions.get().map(this.principalFromOption)
	},

	draw: function(principals, options = {}) {

		// Subsequent searches will reassign this.principals, so we must
		// keep track of the full list of principals here
		if (options._initial) {
			this.allPrincipals = principals
		}
		// remember these for the .reset method
		this.principals = principals
		// add any principals that were previously selected but not in the
		// provided list of principals (#23803)
		let selectedPrincipals = this._selectedPrincipals()
		let principalsToAdd = _.filter(selectedPrincipals, principal => {
			return !_.isEmpty(principal) &&
				principal.id > 0 && // Exclude Admin, Everyone, or any other pseudo principal
				!_.find(principals, p => p.id == principal.id && p.type == principal.type)
		})
		principals = principalsToAdd.concat(principals)

		principals = _.map(principals, principal => {
			principal = _.clone(principal)
			this.populateDomDataset(principal)
			// #23803 when selecting by email address. Shouldn't harm other
			// places where this is used
			if (_.includes(this.$('select').val(), principal.email)) {
				principal.selected = true
			}
			return principal
		})

		clearTimeout(this.loadingTimeout)
		this.clearLoadingDots()

		// Render the select w/ these new principals
		// #23377 don't use _.defaultsDeep b/c we don't want to mix together
		// options.options from two sources
		this._renderSelect(_.defaults({}, options, {
			options: principals,
			open: true // open by default unless told otherwise
		}))
	},

	/**
	 * Removes previously chosen options so the full list of initial options is available.
	 */
	reset: function() {
		this.selectedSet = this.selectedSet.clear()
		this.draw(this.allPrincipals, { open: false })
	},

	/**
	 * Prepares the SELECT OPTIONs that should be rendered.  Filters out any
	 * that were explicitly excluded. Also sorts alphabetically.
	 */
	_prepareOptions: function(selectOptions = []) {
		// get rid of the excluded option(s)
		if (!_.isEmpty(this.excluded)) {
			_.remove(selectOptions, principal => {
				return _.some(this.excluded,e => {
					return principalUtil.equals(principal, e) ||
						(e === 'group' && principal.type === 'group') ||
						(e === 'user' && principal.type === 'user')
				})
			})
		}
		let optionsToAdd = this.selectedSet
		let optionClasses = {}

		// #28782 Get the current options to include their label and CSS class.
		// These will be included in arguments passed to scoreboard-select to
		// ensure each option's respective class(es) are kept each time we
		// re-draw the rich select.
		_.each(this.$('.select-item .items .item'), opt => {
			optionClasses[opt.innerText] = opt.className
		})

		// Make sure any items already selected are marked as selected in the list of options
		// Since we always use a multiple select, we don't need to worry about them showing up in the dropdown.
		// If `hideSelectedOptions` was set to true, they won't appear as selected items in the input.
		selectOptions.forEach(option => {
			let opt = this._createImmutablePrincipal(option)
			let included = this.selectedSet.includes(opt)
			if (included) {
				optionsToAdd = optionsToAdd.delete(opt)
			}
			option.selected = included
			option.clazz = optionClasses[option.label]
		})

		selectOptions = selectOptions.concat(_.map(optionsToAdd.toJS(), option => {
			return _.extend({}, option, { selected: true })
		}))

		selectOptions.forEach(this.populateDomDataset.bind(this))

		// #34671/#33020 don't need to escape label because it is escaped further
		// down the line in selects.js#selects()
		selectOptions = _.map(selectOptions, option =>
			_.extend({}, option))

		this._appendPsueoOptions(selectOptions)
		selects.sortOptions(this.options, selectOptions)

		return selectOptions
	},

	_appendPsueoOptions: function(selectOptions) {
		selectOptions.forEach(this.populateDomDataset.bind(this))
		if (_.result(this.options, 'includeEveryone')) {
			let everyone = {
				id: -1,
				label: i18n.get('alerts.everyone'),
				type: 'group',
				dataset: {type: 'group'}
			}
			if (selectOptions &&
				!this.selectedSet.has(this._createImmutablePrincipal(everyone)) &&
				// #22043 don't add the "Everyone" option twice
				!_.find(selectOptions, {id: -1, type: 'group'})) {
				selectOptions.unshift(everyone)
			}
		}
	},

	_renderSelect: function(options) {
		// #24155 avoid side-effects
		let revisedOptions = _.cloneDeep(options)
		revisedOptions.options = this._prepareOptions(_.get(options, 'options') || [])

		const scoreboardSelectOptions = this.selectOptions(revisedOptions)
		selects(this.$('select'), scoreboardSelectOptions)
	},

	/**
	 * Returns the options to pass to the select
	 */
	selectOptions: function(options) {
		const self = this
		const selfOptions = self.options || {}
		const {groupSortOrder} = selfOptions

		const sifter = new Sifter(options.options)
		return _.defaults({}, options, {
			// when selecting multiple, keep the menu open so the user can add more than one
			stayOpenOnSelect: this.multiple,
			placeholder: _.get(this.options, 'placeholder', i18n.get('common.startTyping')),
			hideSelectedOptions: this.hideSelectedOptions,
			multiple: this.multiple,
			groupBy: this.options.groupBy,
			autofocus: _.get(this.options, 'autofocus', true),
			accessors: {
				'value': 'id'
			},
			// although the results are already filtered on the server, we're
			// also using the sifter here to make sure the options are presented
			// in a logical order
			filter: function (value) {
				if (!options.options) {
					return []
				}
				if (!value) {
					return options.options
				}

				// #23885 option labels are escaped, so also escape the search text
				var results = sifter.search(_.escape(value), {
					conjunction: 'and',
					fields: ['label', 'id'],
					preSorted: !_.isNil(groupSortOrder),
					sort: [{field: 'label', direction: 'asc'}]
				}).items
				var filtered = []
				_.each(results, function(item) {
					var principal = options.options[item.id]
					if (!this.selectedSet.has(this._createImmutablePrincipal(principal))) {
						filtered.push(principal)
					}
				}.bind(this))

				// #25948 normally selects.filtering() takes care of this, but
				// we're overring the `filter` option, so we need to manually
				// run the sort that puts "headerless" options on top
				return selects.applyGlobalSortRules(filtered)
			}.bind(this),
			onSearch(value) {
				self.search(value)
				self.createLoadingDots()
			}
		})
	},

	// handle when a principal is selected by triggering an event, and record
	// that the principal is no longer available for selection
	selectPrincipal: function() {
		// not sending the real principal so we don't have to iterate over the
		// list of principals to find the right one
		var selected = this.$('select option:selected')

		// Determine which option was just selected by diffing the selected options w/ our selectedSet
		let option = _.find(selected, opt => {
			let principal = this.principalFromOption(opt)
			let immutable = this._createImmutablePrincipal(principal)
			return !_.isEmpty(principal) && !this.selectedSet.includes(immutable)
		})

		//if not a multiple select, and the filter is cleared out, emit a null
		//so listeners know the previous selection was cleared out
		if (!this.multiple) {
			this.selectedSet = this.selectedSet.clear()
			if (!option) {
				this.trigger('select', null)
				selects.toggleFocus(this.$('select'), true)
				return
			}
		}

		if (!option) {
			// An option was removed, update `this.selectedSet` with the current options in the native select
			// by creating a new set w/ all the options in the native select
			const principals = selected.get().map(this.principalFromOption)
			this.selectedSet = this._createImmutableSet(principals)
			return
		}

		var principal = this.principalFromOption(option)
		this.selectedSet = this.selectedSet.add(this._createImmutablePrincipal(principal))
		this.trigger('select', principal)

		// reset the focus
		selects.toggleFocus(this.$('select'), true)
	},

	/**
	 * Extracts all principal data from the bound data on the option element
	 * This hooks into the select implementation, knowing the data is bound using d3,
	 * but we do it this way for performance reasons. For example, the hidden `option` doesn't
	 * have innerHTML so it's less work for the DOM in IE.
	 */
	principalFromOption: function(option) {
		let datum = d3.select(option).datum()

		// #34457 (which then caused #35550, requiring email check too)
		// (We're detecting if an option was cleared from the select)
		// datum will have a `dataset` property that exists even when we need
		// to treat it as empty
		if (!datum.id && !datum.email) return {}

		// id, type, group, readOnly, etc
		return cleanEmptyStrings(_.extend({}, datum,{
			// #23286 the label provided to the select has to be rich text, so
			// we need to unescape it here
			label: _.unescape(_.trim(datum.label))
		}))
	},

	/**
	 * record that a principal is not available for selection
	 * (may have been selected in another chooser, e.g. dataset permissions)
	 * @param principal
	 */
	excludePrincipal: function(principal) {
		this.excluded.push(principal)
		this._renderSelect({ options: this.principals })
	},

	/**
	 * Updates the set of selected principals, making all principals available
	 * for selection that are not included in the selected set, or that were
	 * provided in options.exclude
	 *
	 * @param {Principal[]} principals
	 */
	updatePrincipals: function(principals) {
		this.selectedSet = this._createImmutableSet(principals)
		this._renderSelect({ options: this.allPrincipals })
	},

	// record that a principal is again available for selection
	removePrincipal: function(principal) {
		this.selectedSet = this.selectedSet.delete(this._createImmutablePrincipal(principal))
		_.remove(this.excluded, e => principalUtil.equals(principal, e))
		this._renderSelect({ options: this.principals })
	},

	remove: function() {
		SearchView.prototype.remove.apply(this, arguments)
		clearTimeout(this.loadingTimeout)
	}

})

export default MultiPrincipalChooser
export { cleanEmptyStrings }

MultiPrincipalChooser.__filename__ = 'components/multi-principal-chooser.js'
